import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  UsersIcon,
  TruckIcon,
  CurrencyDollarIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PhotoIcon,
  CubeIcon,
  MapPinIcon,
  BellIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import { getDashboardStats, getRecentCustomers, getRecentShipments, invoiceService, resetRevenueData } from '@/services/firebase'
import { collection, onSnapshot, query, orderBy, limit } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { Customer, Shipment, Invoice } from '@/types'
import LoadingSpinner from '@/components/LoadingSpinner'
import ShipmentStatusChart from '@/components/ShipmentStatusChart'

const LiveActivityTable = () => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // This would be replaced with real-time listeners in a production app
    // For now, we'll show a placeholder
    setLoading(false);
  }, []);

  if (loading) {
    return <p className="p-6 text-center text-gray-500">Loading live activity...</p>;
  }

  return (
    <div className="p-6">
      <div className="text-center">
        <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
          <BellIcon className="h-12 w-12" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Live Activity Feed</h3>
        <p className="text-sm text-gray-500 mb-6">
          Real-time updates will appear here. Use the notification bell in the header for instant alerts.
        </p>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">Recent Activities</h4>
          <ul className="text-sm text-blue-800 space-y-2">
            <li>• Customer signups</li>
            <li>• New shipment submissions</li>
            <li>• Payment confirmations</li>
            <li>• Invoice updates</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState<any>(null);
  const [recentCustomers, setRecentCustomers] = useState<Customer[]>([]);
  const [recentShipments, setRecentShipments] = useState<Shipment[]>([]);
  const [outstandingInvoices, setOutstandingInvoices] = useState<Invoice[]>([]);
  const [paidInvoices, setPaidInvoices] = useState<Invoice[]>([]);
  const [pendingInvoices, setPendingInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState('Recent Shipments');
  const [showResetModal, setShowResetModal] = useState(false);
  const [resettingRevenue, setResettingRevenue] = useState(false);

  const handleResetRevenue = async () => {
    try {
      setResettingRevenue(true);
      console.log('🔄 Starting revenue reset from dashboard...');
      
      const result = await resetRevenueData();
      
      if (result.success) {
        const message = result.failedCount 
          ? `✅ ${result.message}\n\nRevenue has been reset to 0 for launch preparation.\n\nNote: ${result.failedCount} invoices failed to update.`
          : `✅ ${result.message}\n\nRevenue has been reset to 0 for launch preparation.`;
          
        alert(message);
        setShowResetModal(false);
        
        // Refresh dashboard data to update button state
        await fetchData();
      }
    } catch (error) {
      console.error('❌ Error resetting revenue from dashboard:', error);
      
      let errorMessage = 'Failed to reset revenue data. Please try again.';
      
      if (error instanceof Error) {
        errorMessage = `Failed to reset revenue data: ${error.message}`;
      } else if (typeof error === 'string') {
        errorMessage = `Failed to reset revenue data: ${error}`;
      }
      
      alert(`❌ ${errorMessage}\n\nPlease check the browser console for more details.`);
    } finally {
      setResettingRevenue(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsData, customersData, shipmentsData, outstandingInvoicesData, paidInvoicesData] = await Promise.all([
        getDashboardStats(),
        getRecentCustomers(6),
        getRecentShipments(5),
        invoiceService.getOutstandingInvoices(),
        invoiceService.getPaidInvoices(),
      ]);
      setStats(statsData);
      setRecentCustomers(customersData);
      setRecentShipments(shipmentsData);
      setOutstandingInvoices(outstandingInvoicesData);
      setPaidInvoices(paidInvoicesData);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Set up real-time listeners for invoices
    const invoicesRef = collection(db, 'invoices');
    
    // Listen for all invoice changes to update stats
    const unsubscribeInvoices = onSnapshot(invoicesRef, async (snapshot) => {
      try {
        setIsUpdating(true);
        
        // Get all invoices from the snapshot
        const allInvoices = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date(),
          dueDate: doc.data().dueDate?.toDate(),
          paidDate: doc.data().paidDate?.toDate(),
        })) as Invoice[];

        // Calculate stats directly from the invoice data
        const outstandingInvoices = allInvoices.filter(
          invoice => invoice.status === 'draft' || invoice.status === 'sent' || invoice.status === 'overdue'
        );
        const paidInvoices = allInvoices.filter(invoice => invoice.status === 'paid');
        const pendingInvoices = allInvoices.filter(invoice => invoice.status === 'pending');

        const outstandingRevenue = outstandingInvoices.reduce((sum, invoice) => sum + (invoice.total || 0), 0);
        const totalEarned = paidInvoices.reduce((sum, invoice) => sum + (invoice.total || 0), 0);
        const pendingRevenue = pendingInvoices.reduce((sum, invoice) => sum + (invoice.total || 0), 0);

        // Get other stats from existing data
        const newStatsData = {
          ...stats,
          outstandingRevenue,
          totalEarned,
          pendingRevenue,
          revenueBreakdown: {
            outstanding: outstandingRevenue,
            earned: totalEarned,
            pending: pendingRevenue,
            monthly: stats?.revenueBreakdown?.monthly || 0
          }
        };

        setStats(newStatsData);
        setOutstandingInvoices(outstandingInvoices);
        setPaidInvoices(paidInvoices);
        setPendingInvoices(pendingInvoices);
      } catch (error) {
        console.error("Failed to update dashboard data:", error);
      } finally {
        setIsUpdating(false);
      }
    });

    // Listen for recent customers
    const customersRef = collection(db, 'customers');
    const customersQuery = query(customersRef, orderBy('createdAt', 'desc'), limit(6));
    const unsubscribeCustomers = onSnapshot(customersQuery, (snapshot) => {
      const customers = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      })) as Customer[];
      setRecentCustomers(customers);
    });

    // Listen for recent shipments
    const shipmentsRef = collection(db, 'shipments');
    const shipmentsQuery = query(shipmentsRef, orderBy('createdAt', 'desc'), limit(5));
    const unsubscribeShipments = onSnapshot(shipmentsQuery, (snapshot) => {
      const shipments = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      })) as Shipment[];
      setRecentShipments(shipments);
    });

    // Cleanup listeners on unmount
    return () => {
      unsubscribeInvoices();
      unsubscribeCustomers();
      unsubscribeShipments();
    };
  }, []);

  const statCards = [
    { name: 'Total Customers', stat: stats?.totalCustomers || '0', icon: UsersIcon },
    { name: 'Total Shipments', stat: stats?.totalShipments || '0', icon: TruckIcon },
    { name: 'Outstanding Revenue', stat: `$${stats?.outstandingRevenue?.toFixed(2) || '0.00'}`, icon: CurrencyDollarIcon },
    { name: 'Total Earned', stat: `$${stats?.totalEarned?.toFixed(2) || '0.00'}`, icon: CurrencyDollarIcon },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            {isUpdating && (
              <div className="flex items-center text-blue-600 text-sm">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                Updating...
              </div>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">Overview of your shipping operations</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/customers/new" className="btn-primary inline-flex items-center">
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Customer
          </Link>
          <Link to="/shipments/new" className="btn-secondary inline-flex items-center">
            <PlusIcon className="h-5 w-5 mr-2" />
            New Shipment
          </Link>
          
          {/* Revenue Reset Button for Launch - Only show when there's revenue to reset */}
          {(() => {
            const hasRevenueToReset = paidInvoices.length > 0 || pendingInvoices.length > 0;
            const hasBeenReset = paidInvoices.length === 0 && pendingInvoices.length === 0 && outstandingInvoices.some(inv => inv.resetForLaunch);
            
            if (hasBeenReset) {
              return (
                <div className="inline-flex items-center px-4 py-2 border border-green-300 text-green-700 bg-green-50 rounded-md">
                  <ArrowPathIcon className="h-5 w-5 mr-2" />
                  Revenue Reset for Launch ✅
                </div>
              );
            }
            
            if (hasRevenueToReset) {
              return (
                <button
                  onClick={() => setShowResetModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-orange-300 text-orange-700 bg-white rounded-md hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
                  title="Reset all revenue data to 0 for launch preparation"
                >
                  <ArrowPathIcon className="h-5 w-5 mr-2" />
                  Reset Revenue for Launch
                </button>
              );
            }
            
            return null; // No button shown when no revenue exists
          })()}
        </div>
      </div>

      {/* Revenue Status Indicator */}
      {(() => {
        const totalRevenue = paidInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
        const pendingRevenue = pendingInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
        const hasBeenReset = paidInvoices.length === 0 && pendingInvoices.length === 0 && outstandingInvoices.some(inv => inv.resetForLaunch);
        
        if (hasBeenReset) {
          return (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <ArrowPathIcon className="h-5 w-5 text-green-600 mr-3" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-green-800">Revenue Reset Complete</h3>
                  <p className="text-sm text-green-700 mt-1">
                    All revenue data has been reset to 0 for launch preparation. 
                    {outstandingInvoices.length > 0 && ` ${outstandingInvoices.length} draft invoices remain.`}
                  </p>
                </div>
              </div>
            </div>
          );
        }
        
        if (totalRevenue > 0 || pendingRevenue > 0) {
          return (
            <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center">
                <CurrencyDollarIcon className="h-5 w-5 text-orange-600 mr-3" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-orange-800">Revenue Data Present</h3>
                  <p className="text-sm text-orange-700 mt-1">
                    {totalRevenue > 0 && `$${totalRevenue.toFixed(2)} in paid invoices`}
                    {totalRevenue > 0 && pendingRevenue > 0 && ' and '}
                    {pendingRevenue > 0 && `$${pendingRevenue.toFixed(2)} in pending invoices`}
                    . Use the "Reset Revenue for Launch" button above to prepare for launch.
                  </p>
                </div>
              </div>
            </div>
          );
        }
        
        return null;
      })()}

      {/* Customer Submission Notifications */}
      {(() => {
        const customerShipments = recentShipments.filter(s => s.createdBy && s.createdBy === s.customerId);
        const pendingPayments = pendingInvoices;

        // Only show notifications when not loading and there's actual data
        if (!loading && (customerShipments.length > 0 || pendingPayments.length > 0)) {
          return (
            <div className="mb-6">
              {customerShipments.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                  <div className="flex items-center">
                    <BellIcon className="h-5 w-5 text-blue-600 mr-3" />
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-blue-800">
                        New Customer Submissions ({customerShipments.length})
                      </h3>
                      <p className="text-sm text-blue-700 mt-1">
                        Customers have submitted {customerShipments.length} new shipment(s) that require your attention.
                      </p>
                    </div>
                    <button 
                      onClick={() => {
                        console.log('Setting active tab to Customer Submissions');
                        setActiveTab('Customer Submissions');
                      }}
                      className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                    >
                      View All
                    </button>
                  </div>
                </div>
              )}
              
              {pendingPayments.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-3" />
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-yellow-800">
                        Pending Payment Confirmations ({pendingPayments.length})
                      </h3>
                      <p className="text-sm text-yellow-700 mt-1">
                        {pendingPayments.length} payment(s) are awaiting your confirmation. Review and approve payments.
                      </p>
                    </div>
                    <button 
                      onClick={() => {
                        console.log('Setting active tab to Pending Revenue');
                        setActiveTab('Pending Revenue');
                      }}
                      className="bg-yellow-600 text-white px-3 py-1 rounded text-sm hover:bg-yellow-700 transition-colors"
                    >
                      Review Payments
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        }
        
        // Show loading state for notifications
        if (loading) {
          return (
            <div className="mb-6">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600 mr-3"></div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-800">Loading notifications...</h3>
                    <p className="text-sm text-gray-600 mt-1">Checking for new submissions and pending payments</p>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        
        return null;
      })()}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        {statCards.map((item) => (
          <div key={item.name} className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <item.icon className="h-6 w-6 text-gray-400" aria-hidden="true" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">{item.name}</dt>
                    <dd className="text-2xl font-semibold text-gray-900">{item.stat}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link 
            to="/customers/new" 
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <UsersIcon className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <h4 className="font-medium text-gray-900">Add New Customer</h4>
              <p className="text-sm text-gray-500">Create customer account</p>
            </div>
          </Link>
          
          <Link 
            to="/shipments/new" 
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <TruckIcon className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <h4 className="font-medium text-gray-900">Create Shipment</h4>
              <p className="text-sm text-gray-500">Add new shipment</p>
            </div>
          </Link>
          
          <Link 
            to="/invoices/new" 
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <CurrencyDollarIcon className="h-8 w-8 text-yellow-600 mr-3" />
            <div>
              <h4 className="font-medium text-gray-900">Generate Invoice</h4>
              <p className="text-sm text-gray-500">Create new invoice</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Shipment Status Overview */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Shipment Status Overview</h3>
            <Link to="/shipments" className="text-sm font-medium text-blue-600">All Shipments ({stats?.totalShipments || 0})</Link>
          </div>
          <div className="mt-4 h-48">
            <ShipmentStatusChart data={stats?.shipmentStatusCounts} />
          </div>
      </div>

      {/* Recent Customers */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Recent Customers</h3>
          <Link to="/customers" className="text-sm font-medium text-blue-600 hover:text-blue-700">
            View All Customers
          </Link>
        </div>
        <div className="relative mb-4">
            <MagnifyingGlassIcon className="pointer-events-none absolute top-1/2 -translate-y-1/2 left-3 h-5 w-5 text-gray-400" />
            <input type="text" placeholder="Search customers..." className="input pl-10 w-full" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {recentCustomers.map((customer) => (
            <div key={customer.id} className="text-center">
              {customer.photoURL ? (
                 <img className="mx-auto h-16 w-16 rounded-full" src={customer.photoURL} alt="" />
              ) : (
                <span className="mx-auto h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-2xl font-medium text-blue-600">{customer.name.charAt(0)}</span>
                </span>
              )}
              <h4 className="mt-2 text-sm font-semibold text-gray-900 truncate">{customer.name}</h4>
              <p className="text-xs text-gray-500 truncate">{customer.email}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity Tabs */}
      <div>
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('Recent Shipments')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'Recent Shipments' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Recent Shipments
            </button>
            <button
              onClick={() => setActiveTab('Customer Submissions')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'Customer Submissions' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Customer Submissions
            </button>
            <button
              onClick={() => setActiveTab('Outstanding Invoices')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'Outstanding Invoices' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Outstanding Invoices
            </button>
            <button
              onClick={() => setActiveTab('Money Earned')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'Money Earned' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Money Earned
            </button>
            <button
              onClick={() => setActiveTab('Pending Revenue')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'Pending Revenue' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Pending Revenue
            </button>
            <button
              onClick={() => setActiveTab('Live Activity')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'Live Activity' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Live Activity
            </button>
          </nav>
        </div>
        <div className="mt-4 bg-white shadow rounded-lg">
          {(() => {
            console.log('Rendering tab content for:', activeTab);
            
            // Add a header showing which tab is active
            const tabHeader = (
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-semibold text-gray-900">
                  {activeTab === 'Recent Shipments' && 'Recent Shipments'}
                  {activeTab === 'Customer Submissions' && 'Customer Submissions'}
                  {activeTab === 'Outstanding Invoices' && 'Outstanding Invoices'}
                  {activeTab === 'Money Earned' && 'Money Earned'}
                  {activeTab === 'Pending Revenue' && 'Pending Payment Confirmations'}
                  {activeTab === 'Live Activity' && 'Live Activity'}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {activeTab === 'Recent Shipments' && 'Latest shipments in your system'}
                  {activeTab === 'Customer Submissions' && 'Shipments created by customers awaiting review'}
                  {activeTab === 'Outstanding Invoices' && 'Invoices that are due or overdue'}
                  {activeTab === 'Money Earned' && 'Revenue from completed payments'}
                  {activeTab === 'Pending Revenue' && 'Payments awaiting your confirmation'}
                  {activeTab === 'Live Activity' && 'Real-time updates and notifications'}
                </p>
              </div>
            );
            
            if (activeTab === 'Recent Shipments') {
              return (
                <>
                  {tabHeader}
                  <ShipmentsTable shipments={recentShipments} />
                </>
              );
            } else if (activeTab === 'Customer Submissions') {
              console.log('Rendering CustomerSubmissionsTable with shipments:', recentShipments.filter(s => s.createdBy && s.createdBy === s.customerId));
              return (
                <>
                  {tabHeader}
                  <CustomerSubmissionsTable shipments={recentShipments.filter(s => s.createdBy && s.createdBy === s.customerId)} />
                </>
              );
            } else if (activeTab === 'Outstanding Invoices') {
              return (
                <>
                  {tabHeader}
                  <InvoicesTable invoices={outstandingInvoices} />
                </>
              );
            } else if (activeTab === 'Money Earned') {
              return (
                <>
                  {tabHeader}
                  <MoneyEarnedTable stats={stats} paidInvoices={paidInvoices} />
                </>
              );
            } else if (activeTab === 'Pending Revenue') {
              console.log('Rendering PendingRevenueTable with invoices:', pendingInvoices);
              return (
                <>
                  {tabHeader}
                  <PendingRevenueTable invoices={pendingInvoices} />
                </>
              );
            } else {
              return (
                <>
                  {tabHeader}
                  <LiveActivityTable />
                </>
              );
            }
          })()}
        </div>
      </div>

      {/* Revenue Reset Confirmation Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100">
                <ArrowPathIcon className="h-6 w-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Reset Revenue for Launch</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 mb-4">
                  Are you sure you want to reset all revenue data to 0 for launch preparation?
                </p>
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-left">
                  <p className="text-sm text-yellow-800 font-medium mb-2">
                    ⚠️ This action will:
                  </p>
                  <ul className="text-sm text-yellow-700 space-y-1">
                    <li>• Reset all paid invoices to draft status</li>
                    <li>• Clear all payment data</li>
                    <li>• Set total revenue to $0.00</li>
                    <li>• <strong>Preserve all customer and invoice data</strong></li>
                    <li>• <strong>Be reversible using restore function</strong></li>
                  </ul>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  This is designed for launch preparation. All data will be preserved and can be restored if needed.
                </p>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={handleResetRevenue}
                  disabled={resettingRevenue}
                  className="px-4 py-2 bg-orange-600 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-300 disabled:opacity-50"
                >
                  {resettingRevenue ? 'Resetting Revenue...' : 'Reset Revenue for Launch'}
                </button>
                <button
                  onClick={() => setShowResetModal(false)}
                  className="mt-2 px-4 py-2 bg-gray-300 text-gray-700 text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ShipmentsTable = ({ shipments }: { shipments: Shipment[] }) => {
  if (shipments.length === 0) {
    return <p className="p-6 text-center text-gray-500">No recent shipments.</p>;
  }
  return (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tracking #</th>
          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
          <th scope="col" className="relative px-6 py-3"><span className="sr-only">View</span></th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {shipments.map((shipment) => (
          <tr key={shipment.id}>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{shipment.trackingNumber}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{shipment.customer?.name || 'Unknown Customer'}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800`}>
                {shipment.status}
              </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                shipment.createdBy && shipment.createdBy === shipment.customerId ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'
              }`}>
                {shipment.createdBy && shipment.createdBy === shipment.customerId ? 'Customer Created' : 'Admin Created'}
              </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
              <Link to={`/shipments/${shipment.id}`} className="text-blue-600 hover:text-blue-900">View</Link>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const CustomerSubmissionsTable = ({ shipments }: { shipments: Shipment[] }) => {
  if (shipments.length === 0) {
    return (
      <div className="p-6 text-center">
        <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
          <TruckIcon className="h-12 w-12" />
        </div>
        <h3 className="text-sm font-medium text-gray-900 mb-2">No Customer Submissions</h3>
        <p className="text-sm text-gray-500">When customers create shipments, they will appear here for your review.</p>
      </div>
    );
  }

  // Debug: Log the shipments to see what data we have
  console.log('Customer submissions shipments:', shipments);

  return (
    <div className="overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Customer Shipment Submissions</h3>
        <p className="text-sm text-gray-500">Detailed view of shipments created by customers</p>
      </div>
      <div className="divide-y divide-gray-200">
        {shipments.map((shipment) => {
          // Debug: Log individual shipment data
          console.log('Shipment data:', shipment);
          console.log('Shipment items:', shipment.items);
          
          return (
            <div key={shipment.id} className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">{shipment.trackingNumber}</h4>
                  <p className="text-sm text-gray-500">Customer: {shipment.customer?.name}</p>
                  <p className="text-sm text-gray-500">Status: {shipment.status}</p>
                  <p className="text-sm text-gray-500">Created: {new Date(shipment.createdAt).toLocaleDateString()}</p>
                </div>
                <div className="flex space-x-2">
                  <Link to={`/shipments/${shipment.id}`} className="btn-secondary text-sm">
                    View Details
                  </Link>
                  <Link to={`/invoices/new?shipmentId=${shipment.id}`} className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700">
                    Create Invoice
                  </Link>
                </div>
              </div>

              {/* Route Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h5 className="font-medium text-gray-900 mb-2 flex items-center">
                    <MapPinIcon className="h-4 w-4 mr-2" />
                    Origin
                  </h5>
                  <p className="text-sm text-gray-600">
                    {shipment.origin?.city || 'N/A'}, {shipment.origin?.state || 'N/A'}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h5 className="font-medium text-gray-900 mb-2 flex items-center">
                    <MapPinIcon className="h-4 w-4 mr-2" />
                    Destination
                  </h5>
                  <p className="text-sm text-gray-600">
                    {shipment.destination?.city || 'N/A'}, {shipment.destination?.state || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Shipment Items */}
              <div className="mb-4">
                <h5 className="font-medium text-gray-900 mb-2 flex items-center">
                  <CubeIcon className="h-4 w-4 mr-2" />
                  Items ({shipment.items?.length || 0})
                </h5>
                {shipment.items && shipment.items.length > 0 ? (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {shipment.items.map((item, index) => (
                        <div key={index} className="bg-white p-3 rounded border">
                          <p className="font-medium text-sm">{item.description || 'No description'}</p>
                          <p className="text-xs text-gray-500">
                            Qty: {item.quantity || 0} | Weight: {item.weight || 0}kg
                          </p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-sm font-medium">
                        Total Weight: {shipment.items.reduce((sum, item) => sum + ((item.weight || 0) * (item.quantity || 0)), 0).toFixed(2)}kg
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">No items specified</p>
                  </div>
                )}
              </div>

              {/* General Description */}
              {shipment.description && (
                <div className="mb-4">
                  <h5 className="font-medium text-gray-900 mb-2">General Description</h5>
                  <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded border-l-4 border-blue-400">
                    {shipment.description}
                  </p>
                </div>
              )}

              {/* Weight and Dimensions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {shipment.weight && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h5 className="font-medium text-gray-900 mb-2">Total Weight</h5>
                    <p className="text-sm text-gray-600">{shipment.weight}kg</p>
                  </div>
                )}
                {shipment.dimensions && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h5 className="font-medium text-gray-900 mb-2">Dimensions</h5>
                    <p className="text-sm text-gray-600">{shipment.dimensions}</p>
                  </div>
                )}
              </div>

              {/* Images */}
              {shipment.images && shipment.images.length > 0 && (
                <div className="mb-4">
                  <h5 className="font-medium text-gray-900 mb-2 flex items-center">
                    <PhotoIcon className="h-4 w-4 mr-2" />
                    Images ({shipment.images.length})
                  </h5>
                  <div className="flex space-x-2 overflow-x-auto">
                    {shipment.images.map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`Shipment image ${index + 1}`}
                        className="h-20 w-20 object-cover rounded border"
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Special Instructions */}
              {shipment.specialInstructions && (
                <div className="mb-4">
                  <h5 className="font-medium text-gray-900 mb-2">Special Instructions</h5>
                  <p className="text-sm text-gray-600 bg-yellow-50 p-3 rounded border-l-4 border-yellow-400">
                    {shipment.specialInstructions}
                  </p>
                </div>
              )}

              {/* Estimated Delivery */}
              <div className="text-sm text-gray-500">
                Estimated Delivery: {new Date(shipment.estimatedDeliveryDate).toLocaleDateString()}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const InvoicesTable = ({ invoices }: { invoices: Invoice[] }) => {
  if (invoices.length === 0) {
    return <p className="p-6 text-center text-gray-500">No outstanding invoices.</p>;
  }
  return (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
          <th scope="col" className="relative px-6 py-3"><span className="sr-only">View</span></th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {invoices.map((invoice) => (
          <tr key={invoice.id}>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{invoice.invoiceNumber}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{invoice.customer?.name || 'Unknown Customer'}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${invoice.total?.toFixed(2) || '0.00'}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : 'N/A'}</td>
            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
              <Link to={`/invoices/${invoice.id}`} className="text-blue-600 hover:text-blue-900">View</Link>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const PendingRevenueTable = ({ invoices }: { invoices: Invoice[] }) => {
  if (invoices.length === 0) {
    return (
      <div className="p-6 text-center">
        <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
          <svg className="h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Payments</h3>
        <p className="text-sm text-gray-500">
          All payments have been processed. New pending payments will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Pending Payment Confirmations</h3>
        <p className="text-sm text-gray-500">
          {invoices.length} payment(s) awaiting confirmation. Review and approve payments to move them to "Money Earned".
        </p>
      </div>

      <div className="space-y-4">
        {invoices.map((invoice) => (
          <div key={invoice.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="font-medium text-gray-900">Invoice #{invoice.invoiceNumber}</h4>
                <p className="text-sm text-gray-500">{invoice.customer?.name || 'Unknown Customer'}</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-orange-600">
                  ${invoice.total?.toFixed(2) || '0.00'}
                </p>
                <p className="text-xs text-gray-500">
                  {invoice.paymentData?.paymentMethod === 'cash' ? 'Cash Payment' : 'Bank Transfer'}
                </p>
              </div>
            </div>
            
            {invoice.paymentData?.paymentMethod === 'bank-transfer' && invoice.paymentData?.receiptImageUrl && (
              <div className="mb-3">
                <p className="text-sm font-medium text-gray-700 mb-2">Payment Receipt:</p>
                <a 
                  href={invoice.paymentData.receiptImageUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <img 
                    src={invoice.paymentData.receiptImageUrl} 
                    alt="Payment Receipt" 
                    className="w-24 h-24 object-cover rounded-lg border border-gray-200 hover:opacity-80 transition-opacity"
                  />
                </a>
                <p className="text-xs text-gray-500 mt-1">Click to view full size</p>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                <p>Payment Date: {invoice.paymentData?.uploadedAt ? new Date(invoice.paymentData.uploadedAt).toLocaleDateString() : 'N/A'}</p>
                <p>Due Date: {new Date(invoice.dueDate).toLocaleDateString()}</p>
              </div>
              <div className="flex space-x-2">
                <Link 
                  to={`/invoices/${invoice.id}`}
                  className="px-3 py-1 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                >
                  View Invoice
                </Link>
                <button 
                  onClick={() => {
                    // This would trigger a function to mark as paid
                    console.log('Mark as paid:', invoice.id);
                  }}
                  className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                >
                  Mark as Paid
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MoneyEarnedTable = ({ stats, paidInvoices }: { stats: any; paidInvoices: Invoice[] }) => {
  if (!stats) {
    return <p className="p-6 text-center text-gray-500">Loading revenue data...</p>;
  }

  return (
    <div className="p-6">
      {/* Revenue Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-green-600">Total Earned</p>
              <p className="text-2xl font-bold text-green-900">${stats.totalEarned?.toFixed(2) || '0.00'}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <CurrencyDollarIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-blue-600">This Month</p>
              <p className="text-2xl font-bold text-blue-900">${stats.monthlyRevenue?.toFixed(2) || '0.00'}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <CurrencyDollarIcon className="h-8 w-8 text-yellow-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-yellow-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-900">${stats.pendingRevenue?.toFixed(2) || '0.00'}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <CurrencyDollarIcon className="h-8 w-8 text-red-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-red-600">Outstanding</p>
              <p className="text-2xl font-bold text-red-900">${stats.outstandingRevenue?.toFixed(2) || '0.00'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Method Breakdown */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Methods</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{stats.paymentMethods?.cash || 0}</div>
            <div className="text-sm text-gray-600">Cash Payments</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{stats.paymentMethods?.bankTransfer || 0}</div>
            <div className="text-sm text-gray-600">Bank Transfers</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-600">{stats.paymentMethods?.other || 0}</div>
            <div className="text-sm text-gray-600">Other Methods</div>
          </div>
        </div>
      </div>

      {/* Paid Invoices Table */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Paid Invoices</h3>
        {paidInvoices.length === 0 ? (
          <p className="text-center text-gray-500">No paid invoices found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Method</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid Date</th>
                  <th scope="col" className="relative px-6 py-3"><span className="sr-only">View</span></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paidInvoices.slice(0, 10).map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{invoice.invoiceNumber}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{invoice.customer?.name || 'Unknown Customer'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">${invoice.total?.toFixed(2) || '0.00'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invoice.paymentData?.paymentMethod === 'cash' ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Cash
                        </span>
                      ) : invoice.paymentData?.paymentMethod === 'bank-transfer' ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          Bank Transfer
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                          Other
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invoice.paidDate ? new Date(invoice.paidDate).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link to={`/invoices/${invoice.id}`} className="text-blue-600 hover:text-blue-900">View</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Revenue Chart Placeholder */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trend</h3>
        <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
          <p className="text-gray-500">Revenue chart will be implemented here</p>
        </div>
      </div>
    </div>
  );
}; 